// One line per IU Rewrite change on a row (row.iuRewrite from the proxy), e.g.
// "IS_TEST_BUILD: 1 → 0", "removed adtest (was on)", "added restricted_to_buyer_network=1418116".
export function describeRewrite(iuRewrite) {
  const changes = (iuRewrite && Array.isArray(iuRewrite.changes)) ? iuRewrite.changes : [];
  return changes.map((c) => {
    if (!c) return '';
    const s = describeOne(c);
    return c.where === 'body' ? `${s} (in body)` : s;
  }).filter(Boolean);
}

function describeOne(c) {
  if (c.field === 'paramRemove' || c.field === 'kvpRemove') return `removed ${c.key}${c.from != null ? ` (was ${c.from})` : ''}`;
  if (c.field === 'paramAdd' || c.field === 'kvpAdd') return c.from != null ? `${c.key}: ${c.from} → ${c.value}` : `added ${c.key}=${c.value}`;
  if (c.field === 'metaTestMode') return c.from != null ? `${c.key}: ${c.from} → ${c.value} (Meta test mode)` : `added ${c.key}=${c.value} (Meta test mode)`;
  if (c.field === 'metaField') return c.from != null ? `${c.key}: ${c.from} → ${c.to}` : `added ${c.key}=${c.to}`;
  return `${c.field}${c.key ? ` ${c.key}` : ''}: ${c.from ?? ''} → ${c.to ?? c.value ?? ''}`;
}

// Rule-list line for a random-ppid rule (paramAdd ppid + randomValue 'uuid'); null for every other
// rule so the page keeps its existing wording. Never throws.
export function describeRule(rule) {
  if (!rule || typeof rule !== 'object') return null;
  if (rule.field === 'paramAdd' && rule.key === 'ppid' && rule.randomValue === 'uuid') {
    return `ppid = random UUID per request (only under ${rule.iuPrefix})`;
  }
  return null;
}

// Client twin of proxy-server/iuRewrite.js randomRuleProblem (same rules, same order). Edit both.
// A non-random draft is always ok. Never throws; returns every problem found.
const IU_PREFIX_RE = /^\/[A-Za-z0-9_.-]+(\/[A-Za-z0-9_.-]+)+$/;

function stripIuPrefix(p) {
  const s = typeof p === 'string' ? p.trim() : '';
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

export function validateRandomPpidDraft(draft) {
  try {
    if (!draft || typeof draft !== 'object' || draft.field !== 'paramAdd') return { ok: true, errors: [], iuPrefix: '' };
    const rv = draft.randomValue;
    if (rv == null || rv === '') return { ok: true, errors: [], iuPrefix: '' };
    const iuPrefix = stripIuPrefix(draft.iuPrefix);
    const errors = [];
    if (rv !== 'uuid') errors.push('Unsupported random value mode.');
    if (String(draft.key == null ? '' : draft.key).trim() !== 'ppid') errors.push('Random value only works with the key ppid.');
    if (!String(draft.device == null ? '' : draft.device).trim()) errors.push('Pick a device scope: a random ppid rule must not apply to every device.');
    if (!IU_PREFIX_RE.test(iuPrefix)) errors.push('Ad unit prefix must start with / and have at least two segments, e.g. /4145/FNC.test (no *, //, ?, spaces).');
    return { ok: errors.length === 0, errors, iuPrefix };
  } catch (_) {
    return { ok: false, errors: ['Invalid random ppid rule.'], iuPrefix: '' };
  }
}

// One line per change a Chaos "patch" or "swap" rule made to this row's response
// (row.chaos.patched / row.chaos.swapped from the proxy), e.g.
// "placements.0.feature_config.x: \"true\" → \"false\"" or "1143…588 → 9999…111 (×2)".
export function describeChaosPatch(chaos) {
  if (chaos && chaos.action === 'swap' && Array.isArray(chaos.swapped)) {
    return chaos.swapped.filter(Boolean).map((c) => `${c.from} → ${c.to}${c.count > 1 ? ` (×${c.count})` : ''}`);
  }
  const changes = (chaos && chaos.action === 'patch' && Array.isArray(chaos.patched)) ? chaos.patched : [];
  return changes.filter(Boolean).map((c) => `${c.path}: ${JSON.stringify(c.from)} → ${JSON.stringify(c.to)}`);
}
